import React, { useMemo, useRef, useEffect, useState, useContext } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { Formik } from 'formik';

import editIcon from 'assets/edit_field.svg';
import cropIcon from 'assets/crop_cycle.svg';

import useCropSeason from 'hooks/useCropSeason';
import { selectCropSeason } from 'utilities/updated-permissions';

import FormNavGuard from 'components/FormNavGuard';
import { Context } from 'components/Store';
import DeleteCropzoneModal from 'screens/Property/PropertiesLanding/components/DeleteCropzoneModal';
import { paths } from 'routes/paths';

import useLandManagementData from 'screens/Property/hooks/useLandManagementData';
import { useFlags } from 'launchdarkly-react-client-sdk';
import featureFlagRenderItem from 'helpers/featureFlagRenderItem';
import useCropSeasonsListApi from 'hooks/useCropSeasonsListApi';
import updateCropZoneGeoJson from 'helpers/updateCropZoneGeoJson';
import useCropZoneData from '../../../hooks/useCropZoneData';
import {
  calculateBoundaryAreaInAcres,
  convertFeaturesToBaseGeometry
} from '../../../helpers/propertyDataHelpers';

import CropZoneHeader from './components/CropZoneHeader';
import CropZoneAction from './components/CropZoneAction';
import CropZoneActions from './components/CropZoneActions';
import CropZoneNameInput from './components/CropZoneNameInput';
import CropZoneAreaInput from './components/CropZoneAreaInput';
import CropZoneCropSelect from './components/CropZoneCropSelect';
import CropZoneCropSeasonSelect from './components/CropZoneCropSeasonSelect';
import schema from './schema';
import featuresToGetGeometry from '../../helpers/featuresToGetGeometry';
import cropZoneGenerator from '../../helpers/cropZoneGenerator';
import handleCropZoneRedirect from '../../helpers/handleCropZoneRedirect';

const Division = () => <div className="border-b mb-2 mt-2 -ml-10 -mr-6" />;

const cropZoneCardDefaultPropFeatures = [];
const cropZoneCardDefaultPropIsSaving = () => {};
const CropZoneCard = ({
  field = null,
  features = cropZoneCardDefaultPropFeatures,
  isSaving = cropZoneCardDefaultPropIsSaving,
  cropzone = null
}) => {
  const { isOrgInFF } = useLandManagementData();

  const history = useHistory();
  const { action, cropzoneId } = useParams();
  const [newCropSeasonId, setNewCropSeasonId] = useState(null);
  const [cropZoneSaved, setCropZoneSaved] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const goTo = useRef(paths.properties);
  const hasLeft = useRef(false);
  const leavesAfterSave = useRef(false);
  const navGuardDisabled = useRef(false);
  const stopSubmitting = useRef(() => {});

  const [{ organization, allFFTGeoJson }, dispatch] = useContext(Context);

  const { cropZone, handleCropZoneSave } = useCropZoneData(cropzoneId);
  const {
    getCropSeason,
    updateCropSeason,
    prepareCropSeason
  } = useCropSeason();

  const {
    releaseFeCondensedCropSeasons,
    releaseFeCondensedCropSeasonsByOrg
  } = useFlags();

  const isCondensedCropSeasonEnabled = featureFlagRenderItem(
    releaseFeCondensedCropSeasons,
    releaseFeCondensedCropSeasonsByOrg,
    organization?.id
  );
  const seasonsApi = useCropSeasonsListApi(isCondensedCropSeasonEnabled);

  const isEditing = action === 'edit';
  const cropSeasonSelected = cropzone?.cropSeasons
    ? cropzone?.cropSeasons[0]?.id
    : '';

  const initialValues = {
    crop: cropZone?.cropId || cropzone?.crop?.id,
    name: cropzone?.name || '',
    reportedArea: cropzone?.reportedArea ?? field?.reportedArea ?? 0,
    cropSeasonId: cropSeasonSelected
  };

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Both the save and the crop season update (orgs without condensed seasons)
  // end by leaving; only the first one navigates.
  const leaveAfterSave = () => {
    if (hasLeft.current) return;
    hasLeft.current = true;
    history.push(goTo.current);
  };

  const saveCropZone = async (
    values,
    redirectToLanding,
    resetForm = () => {}
  ) => {
    const { cropSeasonId } = values;

    isSaving(true);
    leavesAfterSave.current = redirectToLanding;

    const newGeometry = featuresToGetGeometry(features);
    const baseGeometry = convertFeaturesToBaseGeometry(newGeometry);

    baseGeometry.coordinates = _.flatten(baseGeometry.coordinates);

    try {
      const createdCropZone = cropZoneGenerator(
        values,
        field,
        baseGeometry,
        newGeometry
      );

      if (isOrgInFF) {
        createdCropZone.cropSeasonId = cropSeasonId;
      }

      await handleCropZoneSave(isEditing, createdCropZone);

      navGuardDisabled.current = true;
      setCropZoneSaved(true);
      if (createdCropZone.cropSeasonId) {
        await selectCropSeason(
          dispatch,
          [createdCropZone.cropSeasonId],
          isCondensedCropSeasonEnabled,
          seasonsApi
        );
      }
      handleCropZoneRedirect({
        geoJsonData: {
          createdCropZone,
          allFFTGeoJson,
          updateCropZoneGeoJson,
          dispatch
        },
        formActions: {
          resetForm
        },
        navigation: {
          redirectToLanding,
          navigate: leaveAfterSave
        }
      });
      isSaving(false);
    } catch (err) {
      setCropZoneSaved(false);
      isSaving(false);
      stopSubmitting.current();
    }
  };

  const updateCropSeasons = async () => {
    try {
      if (cropSeasonSelected !== newCropSeasonId) {
        if (cropSeasonSelected) {
          const oldCropSeason = await getCropSeason(cropSeasonSelected);
          const updatedOldCropSeason = prepareCropSeason(
            oldCropSeason,
            field?.propertyId,
            cropZone?.id,
            true
          );

          await updateCropSeason(cropSeasonSelected, updatedOldCropSeason);
        }

        const cropSeason = await getCropSeason(newCropSeasonId);
        const updatedCropSeason = prepareCropSeason(
          cropSeason,
          field?.propertyId,
          cropZone?.id
        );

        await updateCropSeason(newCropSeasonId, updatedCropSeason);
      }

      if (leavesAfterSave.current) leaveAfterSave();
    } catch (error) {
      setCropZoneSaved(false);
      isSaving(false);
      stopSubmitting.current();
    }
  };

  useEffect(() => {
    const saveSeasons = async () => {
      if (cropZone && cropZoneSaved) {
        await updateCropSeasons();
      }
    };

    if (!isOrgInFF) {
      saveSeasons();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cropZone, cropZoneSaved]);

  useEffect(() => {
    setNewCropSeasonId(cropSeasonSelected);
  }, [cropSeasonSelected]);

  const validateCanSave = ({ cropSeasonId, crop, name, reportedArea }) => {
    if (!crop) return false;
    if (!name || !_.trim(name)) return false;
    if (!field) return false;
    if (!features.length) return false;
    if (!cropSeasonId) return false;
    if (reportedArea && Number.isNaN(Number.parseFloat(reportedArea)))
      return false;

    return true;
  };

  const boundaryArea = useMemo(() => {
    const area = features.length ? calculateBoundaryAreaInAcres(features) : 0;

    return _.round(area, 2).toFixed(2);
  }, [features]);

  const handleDelete = () => {
    setIsDeleting(true);
    navGuardDisabled.current = true;
    history.push(paths.properties);
  };

  const handleSubmit = async (values, redirectToLanding, resetForm) => {
    setIsMenuOpen(false);
    setNewCropSeasonId(values?.cropSeasonId);
    saveCropZone(
      {
        crop: values.crop,
        name: values.name,
        reportedArea: values.reportedArea,
        cropSeasonId: values.cropSeasonId
      },
      redirectToLanding,
      resetForm
    );
  };

  if (!cropzone) return null;

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={schema}
    >
      {({ values, isSubmitting, resetForm, setSubmitting }) => {
        const canSave = !isSubmitting && validateCanSave(values);
        // The actions save directly, not through Formik's submit.
        const submit = (redirectToLanding, reset) => {
          stopSubmitting.current = () => setSubmitting(false);
          setSubmitting(true);
          handleSubmit(values, redirectToLanding, reset);
        };
        return (
          <div className="flex flex-col h-full">
            <FormNavGuard
              disabled={navGuardDisabled.current}
              isDeleting={isDeleting}
              alwaysShow
            />
            <CropZoneHeader />
            <Division />
            <div className="flex-1 mt-12 flex flex-col gap-12">
              <CropZoneCropSeasonSelect
                cropSeasonId={initialValues.cropSeasonId}
              />
              <CropZoneCropSelect />
              <CropZoneNameInput name={initialValues.name} />
              <CropZoneAreaInput
                boundaryArea={boundaryArea}
                reportedArea={initialValues.reportedArea}
              />
            </div>
            <Division />
            <div className="flex justify-end mt-3 relative">
              <CropZoneActions
                disabled={!canSave}
                onSave={() => submit(true)}
                canDelete={!!cropzone}
                isMenuOpen={isMenuOpen}
                setIsMenuOpen={setIsMenuOpen}
              >
                {isEditing ? (
                  ({ deleteModalOpen, setDeleteModalOpen }) => (
                    <DeleteCropzoneModal
                      open={deleteModalOpen}
                      close={() => setDeleteModalOpen(false)}
                      cropzoneId={cropzone.id}
                      cropzoneName={cropzone.name}
                      onDelete={handleDelete}
                    />
                  )
                ) : (
                  <>
                    <CropZoneAction
                      disabled={!canSave}
                      onClick={() => submit(false, resetForm)}
                    >
                      <img src={cropIcon} alt="" className="mr-2" />
                      <span>Save and Add another Crop zone</span>
                    </CropZoneAction>
                    <CropZoneAction
                      disabled={!canSave}
                      onClick={() => {
                        goTo.current = {
                          pathname: `${paths.properties}/create`,
                          state: {
                            selectedId: field?.propertyId,
                            frameFarmId: field?.propertyId
                          }
                        };
                        submit(true);
                      }}
                    >
                      <img src={editIcon} alt="" className="mr-2" />
                      <span>Save and Draw a new Field</span>
                    </CropZoneAction>
                  </>
                )}
              </CropZoneActions>
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

CropZoneCard.propTypes = {
  isSaving: PropTypes.func,
  field: PropTypes.objectOf,
  features: PropTypes.arrayOf(PropTypes.object),
  cropzone: PropTypes.objectOf
};

export default CropZoneCard;
